import LogRocket from 'logrocket';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

import Enums from '@core/types/api/Enums';
import { hasStringValue } from '@core/utils/equality';
import { PUSH_TOKEN_STORAGE_KEY } from '@web/constants/app';
import { ignoreError } from '@web/utils/common';

import { toast } from '../providers/ToastProvider/ToastEmitter';
import {
  type MessagePayload,
  getMessagingClient,
  getToken,
  isPushSupported,
  onMessage,
  pushConfig,
  registerMessagingServiceWorker,
} from '../third-party/firebase';
import { getFromStorage, removeFromStorage, saveInStorage } from '../utils/storage';
import { useSlabAuth } from './useSlabAuth';
import { useSlabMutation } from './useSlabMutation';

export type PushPermission = 'unsupported' | 'default' | 'granted' | 'denied';

type UsePushNotifications = {
  isPushEnabled: boolean;
  loadingPushStatus: boolean;
  permission: PushPermission;
  refreshPushStatus: () => Promise<void>;
  enablePushNotifications: () => Promise<boolean>;
};

type Params = {
  skipRegistration?: boolean;
};

const readPermission = async (): Promise<PushPermission> => {
  if (!(await isPushSupported())) {
    return 'unsupported';
  }

  return Notification.permission;
};

export const usePushNotifications = ({
  skipRegistration = false,
}: Params = {}): UsePushNotifications => {
  const { user } = useSlabAuth();
  const [{ AuthOverride: authOverride }] = useCookies(['AuthOverride']);
  const { mutateAsync: addDevice } = useSlabMutation('POST push notification device');

  const isImpersonating = (user?.admin ?? false) && (authOverride ?? '') !== '';

  const [permission, setPermission] = useState<PushPermission>('default');
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [loadingPushStatus, setLoadingPushStatus] = useState(true);

  const isRegistering = useRef(false);
  const hasPromptedOnStartup = useRef(false);

  const refreshPushStatus = useCallback(async (): Promise<void> => {
    setLoadingPushStatus(true);
    setPermission(await readPermission());
    setLoadingPushStatus(false);
  }, []);

  useEffect(() => {
    refreshPushStatus().catch(ignoreError);

    if (!('permissions' in navigator)) {
      return undefined;
    }

    let status: PermissionStatus | undefined;
    const onChange = (): void => {
      refreshPushStatus().catch(ignoreError);
    };

    const subscribe = async (): Promise<void> => {
      status = await navigator.permissions.query({ name: 'notifications' });
      status.addEventListener('change', onChange);
    };

    subscribe().catch(ignoreError);

    return (): void => status?.removeEventListener('change', onChange);
  }, [refreshPushStatus]);

  useEffect(() => {
    if (skipRegistration || hasPromptedOnStartup.current) {
      return;
    }
    hasPromptedOnStartup.current = true;

    const promptOnStartup = async (): Promise<void> => {
      if (!(await isPushSupported())) {
        setPermission('unsupported');
        setLoadingPushStatus(false);

        return;
      }

      setPermission(await Notification.requestPermission());
      setLoadingPushStatus(false);
    };

    promptOnStartup().catch(ignoreError);
  }, [skipRegistration]);

  const registerToken = useCallback(async (): Promise<void> => {
    if (pushConfig === null || isRegistering.current || isImpersonating) {
      return;
    }
    isRegistering.current = true;

    try {
      const messaging = getMessagingClient();
      const serviceWorkerRegistration = await registerMessagingServiceWorker();
      if (messaging === null || serviceWorkerRegistration === null) {
        return;
      }

      const token = await getToken(messaging, {
        vapidKey: pushConfig.vapidKey,
        serviceWorkerRegistration,
      });
      if (!hasStringValue(token)) {
        LogRocket.error('Failed to get a web push token');

        return;
      }

      setIsPushEnabled(true);

      if (token === getFromStorage<string>(PUSH_TOKEN_STORAGE_KEY, '')) {
        return;
      }

      await addDevice({ args: { body: { device: Enums.PushNotificationDeviceType.Web, token } } });
      saveInStorage(PUSH_TOKEN_STORAGE_KEY, token);
    } catch (error) {
      removeFromStorage(PUSH_TOKEN_STORAGE_KEY);
      setIsPushEnabled(false);
      LogRocket.error('Failed to register web push device', error);
    } finally {
      isRegistering.current = false;
    }
  }, [addDevice, isImpersonating]);

  useEffect(() => {
    if (permission !== 'granted') {
      setIsPushEnabled(false);

      return;
    }

    if (skipRegistration) {
      setIsPushEnabled(true);

      return;
    }

    registerToken().catch(ignoreError);
  }, [permission, registerToken, skipRegistration]);

  useEffect(() => {
    if (!isPushEnabled || skipRegistration) {
      return undefined;
    }

    const messaging = getMessagingClient();
    if (messaging === null) {
      return undefined;
    }

    // The notification center refetches on the websocket event; the push only raises the toast.
    return onMessage(messaging, (payload: MessagePayload) => {
      toast.info({
        title: payload.notification?.title ?? 'New notification',
        message: payload.notification?.body ?? '',
      });
    });
  }, [isPushEnabled, skipRegistration]);

  const enablePushNotifications = useCallback(async (): Promise<boolean> => {
    if (!(await isPushSupported())) {
      setPermission('unsupported');

      return false;
    }

    const result = await Notification.requestPermission();
    setPermission(result);

    return result === 'granted';
  }, []);

  return {
    isPushEnabled,
    loadingPushStatus,
    permission,
    refreshPushStatus,
    enablePushNotifications,
  };
};
